import { type ExtensionApi } from '../../../core/extension';
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'contrastDoubleBar',
  name_en: 'contrastDoubleBar',
  // name_zh: '对比柱中柱状图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 1,
    z: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    z: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    }
  }
};

/**
 * ECharts 的配置项
 * @see https://echarts.apache.org/zh/option.html#series-line
 */
// type EChartsPieSeries = {
//   roseType?: unknown;
//   radius?: unknown;
// };

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    z: string;
  };
}

export const contrastDoubleBar = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec) {
    const { data: dataArray, util: utils } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, z }
    } = spec;
    const data = dataArray[dataIndex];
    const transformData = utils.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' }
    ]);
    const { x: xAxisData } = utils.dataEncoding.encoding(transformData, {
      x: {
        field: x,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      y: { field: y[0], transforms: [{ type: 'toNumeric' }] },
      z: { field: z, transforms: [{ type: 'deDuplication' }] }
    });
    const zAxis = Array.from(new Set(dataArray[0].values.map(item => item[z])));
    const series = zAxis.map((item, index) => {
      return !index
        ? {
            name: item,
            data: dataArray[0].values.filter(ele => ele[z] === item).map(item => item[y]),
            type: 'bar'
          }
        : {
            name: item,
            itemStyle: {
              color: '#3DB4CC'
            },
            data: dataArray[0].values.filter(ele => ele[z] === item).map(item => item[y]),
            type: 'custom',
            renderItem(params, api) {
              const xValue = api.value(0);
              const highPoint = api.coord([xValue, api.value(1)]);
              const lowPoint = api.coord([xValue, '']);
              const barLayout = api.barLayout({
                count: 1
              });
              const interpoleHeight = Math.abs(highPoint[1] - lowPoint[1]);
              const halfWidth = barLayout[0].width * 0.25;
              const barStyle = api.style({
                fill: 'rgba(61,180,204,0.8)',
                stroke: '#3DB4CC'
              });
              return {
                type: 'group',
                children: [
                  {
                    type: 'rect',
                    transition: ['shape'],
                    shape: {
                      x: highPoint[0] - halfWidth,
                      y: highPoint[1],
                      width: halfWidth * 2,
                      height: interpoleHeight
                    },
                    style: barStyle
                  }
                ]
              };
            },
            z: 100
          };
    });
    /**
     *  配置Series
     * @returns
     */
    function configureSeriesOption() {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        xAxis: {
          show: true,
          type: 'category',
          data: xAxisData,
          axisPointer: {
            show: true,
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(172,198,251)',
              opacity: 0.3
            }
          }
        },
        yAxis: {
          show: true,
          type: 'value',
          __dataIndex: 0,
          __encodeField: [y]
        },
        color: ['#4D5999', '#3DB4CC'],
        series
      };
    }
    // * -------------------------- chart
    return renderApi => {
      const renderParams = {
        option: renderApi.mergeChartOptionProxy(configureSeriesOption())
      };
      renderApi.renderLegendProxy(renderParams.option.legend.data);
      renderApi.renderChartProxy(renderParams);
    };
  }
});
